import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import useField, { UseFieldType, useFieldInitialState } from './useField';
import { IContact } from '@trii/types/dist/Contacts';
import {
  useNotifications,
  UseNotificationsType,
  useNotificationsInitialDef,
} from './useNotifications';

// Types
export interface UseDateState {
  state: {
    selectedStartDate: Dayjs;
    selectedEndDate: Dayjs;
    error: boolean;
  };
  action: {
    setSelectedStartDate: (date: Dayjs) => void;
    setSelectedEndDate: (date: Dayjs) => void;
    setError: (error: boolean) => void;
    handleSelect: (setter: (date: Dayjs) => void) => (date: Dayjs) => void;
  };
}

export interface SelectedContact {
  id: string;
  name: string;
}

export interface UseLinkContact {
  state: {
    search: string;
    selectedContact: SelectedContact | null;
  };
  action: {
    handleSelectContact: (contact: IContact) => void;
    handleRemoveContact: () => void;
    handleSearchChange: (search: string) => void;
    resetField: () => void;
  };
}

export interface UseEvent {
  titleField: UseFieldType;
  descriptionField: UseFieldType;
  locationField: UseFieldType;
  colorField: UseFieldType;
  date: UseDateState;
  allDay: boolean;
  setAllDay: (value: boolean) => void;
  isPrivate: boolean;
  setIsPrivate: (value: boolean) => void;
  recurrenceRule: string;
  setRecurrenceRule: (rule: string) => void;
  notifications: UseNotificationsType;
  linkContact: UseLinkContact;
  create: () => {
    title: string;
    details: string;
    location: string;
    allDay: boolean;
    isPrivate: boolean;
    color: string;
    startAt: string;
    endAt: string;
    notifications: any[];
    rule: string;
    contactId: string | null;
  };
  resetFields: () => void;
}

// Initial State
const useDateStateInitial: UseDateState = {
  state: {
    selectedStartDate: dayjs(),
    selectedEndDate: dayjs().add(30, 'minute'),
    error: false,
  },
  action: {
    setSelectedStartDate: () => {},
    setSelectedEndDate: () => {},
    setError: () => {},
    handleSelect: () => () => {},
  },
};

const useLinkContactInitial: UseLinkContact = {
  state: {
    search: '',
    selectedContact: null,
  },
  action: {
    handleSelectContact: () => {},
    handleRemoveContact: () => {},
    handleSearchChange: () => {},
    resetField: () => {},
  },
};

export const useEventInitialState: UseEvent = {
  titleField: useFieldInitialState,
  descriptionField: useFieldInitialState,
  locationField: useFieldInitialState,
  colorField: useFieldInitialState,
  date: useDateStateInitial,
  allDay: false,
  setAllDay: () => {},
  isPrivate: false,
  setIsPrivate: () => {},
  recurrenceRule: '',
  setRecurrenceRule: () => {},
  notifications: useNotificationsInitialDef,
  linkContact: useLinkContactInitial,
  create: () => ({
    title: '',
    details: '',
    location: '',
    allDay: false,
    isPrivate: false,
    color: '',
    startAt: '',
    endAt: '',
    notifications: [],
    rule: '',
    contactId: null,
  }),
  resetFields: () => {},
};

// Custom Hook para gestionar los estados del Date
const useDateState = (initialStart?: Dayjs, initialEnd?: Dayjs): UseDateState => {
  const [selectedStartDate, setSelectedStartDate] = useState<Dayjs>(initialStart ?? dayjs());
  const [selectedEndDate, setSelectedEndDate] = useState<Dayjs>(
    initialEnd ?? dayjs().add(30, 'minute'),
  );
  const [error, setError] = useState(false);

  const handleSelect = useCallback(
    (setter: (date: Dayjs) => void) => (date: Dayjs) => {
      setter(date);
    },
    [],
  );

  const state = useMemo(
    () => ({
      selectedStartDate,
      selectedEndDate,
      error,
    }),
    [selectedStartDate, selectedEndDate, error],
  );

  const action = useMemo(
    () => ({
      setSelectedStartDate,
      setSelectedEndDate,
      setError,
      handleSelect,
    }),
    [handleSelect],
  );

  return {
    state,
    action,
  };
};

// Custom Hook para gestionar contacto relacionado (single-select)
const useLinkContactState = (initialContact?: { id: string; name: string } | null): UseLinkContact => {
  const [search, setSearch] = useState('');
  const [selectedContact, setSelectedContact] = useState<SelectedContact | null>(
    initialContact?.id ? { id: initialContact.id, name: initialContact.name } : null
  );

  // Sincronizar cuando cambia initialContact (usando key para detectar cambios en objetos)
  const contactKey = initialContact ? `${initialContact.id}|${initialContact.name}` : '';
  useEffect(() => {
    setSelectedContact(
      initialContact?.id ? { id: initialContact.id, name: initialContact.name } : null
    );
  }, [contactKey, initialContact]);

  const handleSelectContact = useCallback((contact: IContact) => {
    setSelectedContact({ id: contact.id, name: contact.name ?? '' });
    setSearch('');
  }, []);

  const handleRemoveContact = useCallback(() => {
    setSelectedContact(null);
  }, []);

  const handleSearchChange = useCallback((newSearch: string) => {
    setSearch(newSearch);
  }, []);

  // Resetear al valor inicial (como useField) para que al cerrar/abrir el modal se restaure correctamente
  const resetField = useCallback(() => {
    setSearch('');
    setSelectedContact(
      initialContact?.id ? { id: initialContact.id, name: initialContact.name } : null
    );
  }, [initialContact]);

  const state = useMemo(
    () => ({
      search,
      selectedContact,
    }),
    [search, selectedContact],
  );

  const action = useMemo(
    () => ({
      handleSelectContact,
      handleRemoveContact,
      handleSearchChange,
      resetField,
    }),
    [handleSelectContact, handleRemoveContact, handleSearchChange, resetField],
  );

  return {
    state,
    action,
  };
};

// Hook principal
const useEvent = (selectedEvent?: any): UseEvent => {
  // 🔒 Ref para trackear el último evento cargado y evitar loops
  const lastEventIdRef = useRef<string | null>(null);

  // Campos de texto
  const titleField = useField('text', selectedEvent?.title ?? '');
  const descriptionField = useField(
    'text',
    selectedEvent?.details ?? selectedEvent?.description ?? '',
  );
  const locationField = useField('text', selectedEvent?.location ?? '');
  const colorField = useField('text', selectedEvent?.color ?? '#1976d2');

  // Estado de fechas
  const date = useDateState(
    selectedEvent?.startAt ? dayjs(selectedEvent.startAt) : undefined,
    selectedEvent?.endAt ? dayjs(selectedEvent.endAt) : undefined,
  );

  // Estados booleanos
  const [allDay, setAllDay] = useState(selectedEvent?.allDay ?? false);
  const [isPrivate, setIsPrivate] = useState(selectedEvent?.isPrivate ?? false);

  // Recurrencia
  const [recurrenceRule, setRecurrenceRule] = useState(selectedEvent?.rule ?? '');

  // Notificaciones
  const notificationsState = useNotifications(selectedEvent?.notifications);

  // Contacto relacionado
  const linkContact = useLinkContactState(
    selectedEvent?.relatedContact ?? selectedEvent?.raw?.relatedContact
  );

  // Crear payload para enviar al backend
  const create = useCallback(() => {
    return {
      title: titleField.attributes.value,
      details: descriptionField.attributes.value,
      location: locationField.attributes.value,
      allDay,
      isPrivate,
      color: colorField.attributes.value,
      startAt: date.state.selectedStartDate.toISOString(),
      endAt: date.state.selectedEndDate.toISOString(),
      notifications: notificationsState.notifications
        .filter((n) => n.timeValue > 0) // Filtrar notificaciones con valor 0 o negativo
        .map((n) => ({
          id: n.id,
          timePeriod: n.timePeriod,
          timeValue: n.timeValue,
          sendToContact: n.sendToContact,
        })),
      rule: recurrenceRule,
      contactId: linkContact.state.selectedContact?.id || null,
    };
  }, [
    titleField.attributes.value,
    descriptionField.attributes.value,
    locationField.attributes.value,
    allDay,
    isPrivate,
    colorField.attributes.value,
    date.state.selectedStartDate,
    date.state.selectedEndDate,
    notificationsState.notifications,
    recurrenceRule,
    linkContact.state.selectedContact,
  ]);

  // Resetear todos los campos
  const resetFields = useCallback(() => {
    titleField.actions.resetValue();
    descriptionField.actions.resetValue();
    locationField.actions.resetValue();
    colorField.actions.changeValue('#1976d2');
    setAllDay(false);
    setIsPrivate(false);
    setRecurrenceRule('');
    notificationsState.action.resetNotifications();
    linkContact.action.resetField();
    date.action.setError(false);
    // Resetear ref para permitir re-sync al abrir el mismo evento nuevamente
    lastEventIdRef.current = null;
  }, [
    titleField.actions,
    descriptionField.actions,
    locationField.actions,
    colorField.actions,
    notificationsState.action,
    linkContact.action,
    date.action,
  ]);

  // 🔄 SINCRONIZACIÓN: Actualizar estados cuando selectedEvent cambia
  // Esto previene problemas al cambiar de modo view → edit → duplicate
  useEffect(() => {
    const currentEventId = selectedEvent?.id;

    // 🔒 PROTECCIÓN CONTRA LOOPS: Solo sincronizar si el ID del evento cambió
    if (!selectedEvent || lastEventIdRef.current === currentEventId) {
      return;
    }

    lastEventIdRef.current = currentEventId;

    // Sincronizar estados booleanos
    setAllDay(selectedEvent.allDay ?? false);
    setIsPrivate(selectedEvent.isPrivate ?? false);
    setRecurrenceRule(selectedEvent.rule ?? '');

    // Sincronizar campos de texto
    titleField.actions.changeValue(selectedEvent.title ?? '');
    descriptionField.actions.changeValue(selectedEvent.details ?? selectedEvent.description ?? '');
    locationField.actions.changeValue(selectedEvent.location ?? '');
    colorField.actions.changeValue(selectedEvent.color ?? '#1976d2');

    // Sincronizar fechas
    if (selectedEvent.startAt) {
      date.action.setSelectedStartDate(dayjs(selectedEvent.startAt));
    }
    if (selectedEvent.endAt) {
      date.action.setSelectedEndDate(dayjs(selectedEvent.endAt));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent?.id]);

  return useMemo(
    () => ({
      titleField,
      descriptionField,
      locationField,
      colorField,
      date,
      allDay,
      setAllDay,
      isPrivate,
      setIsPrivate,
      recurrenceRule,
      setRecurrenceRule,
      notifications: notificationsState,
      linkContact,
      create,
      resetFields,
    }),
    [
      titleField,
      descriptionField,
      locationField,
      colorField,
      date,
      allDay,
      isPrivate,
      recurrenceRule,
      notificationsState,
      linkContact,
      create,
      resetFields,
    ],
  );
};

export default useEvent;
